import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Divider,
  Checkbox,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Science as ScienceIcon,
  LocalHospital as BloodIcon,
  Visibility as RadiologyIcon,
  Psychology as PathologyIcon,
  MonitorHeart as CardiologyIcon,
  Biotech as BiochemistryIcon,
  Coronavirus as MicrobiologyIcon,
  Healing as ImmunologyIcon,
  CurrencyRupee as CurrencyIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Inventory as PackageIcon,
  LocalHospital as MedicalIcon,
  Star as StarIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { collection, query, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

// Types
interface Test {
  id: string;
  name: string;
  category: 'Blood Test' | 'Radiology' | 'Pathology' | 'Cardiology' | 'Biochemistry' | 'Microbiology' | 'Immunology' | 'Health Package' | 'Imaging' | 'Cardiac' | 'Specialized' | 'Urine Test' | 'Stool Test' | 'Emergency';
  price: number;
  description: string;
  preparationInstructions?: string;
  reportTime: string;
  isActive: boolean;
}

interface Package {
  id: string;
  name: string;
  description: string;
  tests: Test[];
  originalPrice: number;
  discountedPrice: number;
  discount: number;
  category: 'Health Checkup' | 'Cardiac' | 'Diabetes' | 'Women Health' | 'Senior Citizen' | 'Corporate';
  duration: string;
  isPopular: boolean;
  ageGroup: string;
  benefits: string[];
  services?: string[];
  isActive: boolean;
}

const Tests: React.FC = () => {
  // State
  const [tests, setTests] = useState<Test[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [filteredTests, setFilteredTests] = useState<Test[]>([]);
  const [filteredPackages, setFilteredPackages] = useState<Package[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedPackageCategory, setSelectedPackageCategory] = useState<string>('All');
  const [selectedTest, setSelectedTest] = useState<Test | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [showAddTestModal, setShowAddTestModal] = useState(false);
  const [showEditTestModal, setShowEditTestModal] = useState(false);
  const [showViewTestModal, setShowViewTestModal] = useState(false);
  const [showAddPackageModal, setShowAddPackageModal] = useState(false);
  const [showEditPackageModal, setShowEditPackageModal] = useState(false);
  const [showViewPackageModal, setShowViewPackageModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [unsubscribeTests, setUnsubscribeTests] = useState<(() => void) | null>(null);
  const [unsubscribePackages, setUnsubscribePackages] = useState<(() => void) | null>(null);

  // Test form state
  const [testForm, setTestForm] = useState({
    name: '',
    category: 'Blood Test' as Test['category'],
    price: '',
    description: '',
    preparationInstructions: '',
    reportTime: '',
    department: '',
    sampleRequired: '',
    testDuration: '30 minutes',
    isActive: true,
    requiresAppointment: false
  });

  // Package form state
  const [packageForm, setPackageForm] = useState({
    name: '',
    description: '',
    selectedTests: [] as Test[],
    originalPrice: '',
    discountedPrice: '',
    discount: '',
    category: 'Health Checkup' as Package['category'],
    duration: '',
    ageGroup: '',
    benefits: [] as string[],
    services: [] as string[],
    isPopular: false
  });

  // Mock data
  const mockTests: Test[] = [
    // Blood Tests
    {
      id: '1',
      name: 'Complete Blood Count (CBC)',
      category: 'Blood Test',
      price: 300,
      description: 'Complete Blood Count with Differential',
      preparationInstructions: 'No special preparation required',
      reportTime: 'Same day',
      isActive: true
    },
    {
      id: '2',
      name: 'ESR (Erythrocyte Sedimentation Rate)',
      category: 'Blood Test',
      price: 150,
      description: 'Inflammation marker test',
      preparationInstructions: 'No special preparation required',
      reportTime: '2 hours',
      isActive: true
    },
    {
      id: '3',
      name: 'Blood Sugar (Fasting)',
      category: 'Blood Test',
      price: 100,
      description: 'Fasting blood glucose test',
      preparationInstructions: '8-12 hours fasting required',
      reportTime: 'Same day',
      isActive: true
    },
    {
      id: '4',
      name: 'Blood Sugar (Random)',
      category: 'Blood Test',
      price: 80,
      description: 'Random blood glucose test',
      preparationInstructions: 'No special preparation required',
      reportTime: 'Same day',
      isActive: true
    },
    {
      id: '5',
      name: 'HbA1c (Glycated Hemoglobin)',
      category: 'Blood Test',
      price: 400,
      description: '3-month average blood sugar',
      preparationInstructions: 'No fasting required',
      reportTime: 'Same day',
      isActive: true
    },
    
    // Imaging/Radiology
    {
      id: '6',
      name: 'X-Ray Chest (PA View)',
      category: 'Radiology',
      price: 300,
      description: 'Chest radiography - Posterior-Anterior view',
      preparationInstructions: 'Remove metal objects from chest area',
      reportTime: '2 hours',
      isActive: true
    },
    {
      id: '7',
      name: 'X-Ray (Bone/Joint)',
      category: 'Radiology',
      price: 250,
      description: 'Bone or joint radiography',
      preparationInstructions: 'Remove metal objects from area',
      reportTime: '2 hours',
      isActive: true
    },
    {
      id: '8',
      name: 'Ultrasound (Abdomen)',
      category: 'Radiology',
      price: 800,
      description: 'Abdominal ultrasound examination',
      preparationInstructions: '6 hours fasting, full bladder',
      reportTime: 'Same day',
      isActive: true
    },
    {
      id: '9',
      name: 'CT Scan (Head)',
      category: 'Radiology',
      price: 2500,
      description: 'Computed Tomography of Head',
      preparationInstructions: 'Remove metal objects, may need contrast',
      reportTime: 'Same day',
      isActive: true
    },
    {
      id: '10',
      name: 'MRI Scan',
      category: 'Radiology',
      price: 5000,
      description: 'Magnetic Resonance Imaging',
      preparationInstructions: 'Remove all metal objects, inform about implants',
      reportTime: 'Same day',
      isActive: true
    },
    
    // Cardiac Tests
    {
      id: '11',
      name: 'ECG (Electrocardiogram)',
      category: 'Cardiology',
      price: 200,
      description: 'Heart rhythm and electrical activity',
      preparationInstructions: 'Avoid caffeine 2 hours before test',
      reportTime: 'Immediate',
      isActive: true
    },
    {
      id: '12',
      name: 'Echo (Echocardiogram)',
      category: 'Cardiology',
      price: 1200,
      description: 'Heart ultrasound examination',
      preparationInstructions: 'No special preparation required',
      reportTime: 'Same day',
      isActive: true
    },
    {
      id: '13',
      name: 'Stress Test (TMT)',
      category: 'Cardiology',
      price: 1500,
      description: 'Treadmill stress test',
      preparationInstructions: 'Comfortable clothes, avoid heavy meal 3 hours before',
      reportTime: 'Same day',
      isActive: true
    },
    {
      id: '14',
      name: 'Holter Monitoring',
      category: 'Cardiology',
      price: 2000,
      description: '24-hour heart rhythm monitoring',
      preparationInstructions: 'Normal daily activities',
      reportTime: '24-48 hours',
      isActive: true
    },
    
    // Specialized Tests
    {
      id: '15',
      name: 'Thyroid Profile (Complete)',
      category: 'Biochemistry',
      price: 600,
      description: 'TSH, T3, T4, Free T3, Free T4',
      preparationInstructions: 'No special preparation required',
      reportTime: 'Same day',
      isActive: true
    },
    {
      id: '16',
      name: 'Lipid Profile',
      category: 'Biochemistry',
      price: 400,
      description: 'Cholesterol, Triglycerides, HDL, LDL',
      preparationInstructions: '12 hours fasting required',
      reportTime: 'Same day',
      isActive: true
    },
    {
      id: '17',
      name: 'Liver Function Test (LFT)',
      category: 'Biochemistry',
      price: 500,
      description: 'SGOT, SGPT, Bilirubin, Albumin',
      preparationInstructions: 'No special preparation required',
      reportTime: 'Same day',
      isActive: true
    },
    {
      id: '18',
      name: 'Kidney Function Test (KFT)',
      category: 'Biochemistry',
      price: 400,
      description: 'Creatinine, BUN, eGFR',
      preparationInstructions: 'No special preparation required',
      reportTime: 'Same day',
      isActive: true
    },
    {
      id: '19',
      name: 'Vitamin D3',
      category: 'Biochemistry',
      price: 800,
      description: '25-hydroxy Vitamin D test',
      preparationInstructions: 'No special preparation required',
      reportTime: '2-3 days',
      isActive: true
    },
    {
      id: '20',
      name: 'Vitamin B12',
      category: 'Biochemistry',
      price: 600,
      description: 'Serum Vitamin B12 levels',
      preparationInstructions: 'No special preparation required',
      reportTime: '2-3 days',
      isActive: true
    }
  ];

  const mockPackages: Package[] = [
    {
      id: '1',
      name: 'Basic Health Checkup',
      description: 'Complete basic health screening package',
      tests: [mockTests[0], mockTests[1], mockTests[5]], // CBC, Lipid Profile, Thyroid Profile
      originalPrice: 1400,
      discountedPrice: 999,
      discount: 29,
      category: 'Health Checkup',
      duration: '4-6 hours',
      isPopular: true,
      ageGroup: '18-60 years',
      benefits: ['Early disease detection', 'Complete blood analysis', 'Thyroid function check', 'Cholesterol screening'],
      isActive: true
    },
    {
      id: '2',
      name: 'Cardiac Health Package',
      description: 'Comprehensive heart health assessment',
      tests: [mockTests[3], mockTests[1], mockTests[2]], // ECG, Lipid Profile, Chest X-Ray
      originalPrice: 1100,
      discountedPrice: 850,
      discount: 23,
      category: 'Cardiac',
      duration: '3-4 hours',
      isPopular: true,
      ageGroup: '30+ years',
      benefits: ['Heart function analysis', 'Cholesterol check', 'Cardiac imaging', 'Risk assessment'],
      isActive: true
    },
    {
      id: '3',
      name: 'Women Health Package',
      description: 'Specialized health screening for women',
      tests: [mockTests[0], mockTests[5]], // CBC, Thyroid Profile
      originalPrice: 900,
      discountedPrice: 699,
      discount: 22,
      category: 'Women Health',
      duration: '3-4 hours',
      isPopular: true,
      ageGroup: '18-65 years',
      benefits: ['Reproductive health check', 'Hormonal analysis', 'Anemia screening'],
      isActive: true
    },
    {
      id: '4',
      name: 'Corporate Health Package',
      description: 'Executive health checkup for professionals',
      tests: [mockTests[0], mockTests[1], mockTests[3]], // CBC, Lipid Profile, ECG
      originalPrice: 1000,
      discountedPrice: 799,
      discount: 20,
      category: 'Corporate',
      duration: '4-5 hours',
      isPopular: false,
      ageGroup: '25-60 years',
      benefits: ['Stress-related health check', 'Cardiovascular screening', 'Fitness assessment'],
      isActive: true
    }
  ];

  const categories = ['All', 'Blood Test', 'Radiology', 'Pathology', 'Cardiology', 'Biochemistry', 'Microbiology', 'Immunology'];
  const packageCategories = ['All', 'Health Checkup', 'Cardiac', 'Diabetes', 'Women Health', 'Senior Citizen', 'Corporate'];

  // Load tests from Firebase with real-time updates
  useEffect(() => {
    const setupTestsData = async () => {
      try {
        setIsLoading(true);
        console.log('🔄 Setting up real-time tests data from Firebase...');
        
        // Set up real-time Firebase listener for tests
        const q = query(collection(db, 'tests'));
        const unsubscribeFn = onSnapshot(q, (querySnapshot) => {
          const firebaseTests: Test[] = [];
          querySnapshot.forEach((doc) => {
            firebaseTests.push({
              id: doc.id,
              ...doc.data()
            } as Test);
          });
          console.log('🧪 Received tests update from Firebase:', firebaseTests.length);
          setTests(firebaseTests);
          setIsLoading(false);
          console.log('✅ Tests data updated from Firebase');
        });
        
        setUnsubscribeTests(() => unsubscribeFn);
        
      } catch (error) {
        console.error('❌ Error setting up tests data:', error);
        setIsLoading(false);
        setSnackbar({ 
          open: true, 
          message: 'Failed to load tests data', 
          severity: 'error' 
        });
      }
    };

    setupTestsData();

    // Cleanup function
    return () => {
      if (unsubscribeTests) {
        console.log('🧹 Cleaning up tests listener...');
        unsubscribeTests();
      }
    };
  }, []);

  // Load health packages from Firebase with real-time updates
  useEffect(() => {
    const setupPackagesData = async () => {
      try {
        console.log('🔄 Setting up real-time health packages data from Firebase...');
        
        // Set up real-time Firebase listener for packages
        const q = query(collection(db, 'healthPackages'));
        const unsubscribeFn = onSnapshot(q, (querySnapshot) => {
          const firebasePackages: Package[] = [];
          querySnapshot.forEach((doc) => {
            firebasePackages.push({
              id: doc.id,
              ...doc.data()
            } as Package);
          });
          console.log('📦 Received health packages update from Firebase:', firebasePackages.length);
          setPackages(firebasePackages);
          console.log('✅ Health packages data updated from Firebase');
        });
        
        setUnsubscribePackages(() => unsubscribeFn);
        
      } catch (error) {
        console.error('❌ Error setting up health packages data:', error);
        setSnackbar({ 
          open: true, 
          message: 'Failed to load health packages data', 
          severity: 'error' 
        });
      }
    };

    setupPackagesData();

    // Cleanup function
    return () => {
      if (unsubscribePackages) {
        console.log('🧹 Cleaning up health packages listener...');
        unsubscribePackages();
      }
    };
  }, []);

  useEffect(() => {
    if (activeTab === 0) {
      filterTests();
    } else if (activeTab === 1) {
      filterPackages();
    }
  }, [searchTerm, selectedCategory, selectedPackageCategory, tests, packages, activeTab]);

  // Functions
  const filterTests = () => {
    let filtered = tests;

    if (searchTerm) {
      filtered = filtered.filter(test => 
        test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(test => test.category === selectedCategory);
    }

    setFilteredTests(filtered);
  };

  const filterPackages = () => {
    let filtered = packages;

    if (searchTerm) {
      filtered = filtered.filter(pkg => 
        pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedPackageCategory !== 'All') {
      filtered = filtered.filter(pkg => pkg.category === selectedPackageCategory);
    }

    setFilteredPackages(filtered);
  };

  // Manual refresh functions
  const handleRefreshTests = async () => {
    try {
      setIsRefreshing(true);
      
      // Reset filters to show all data
      setSearchTerm('');
      setSelectedCategory('All');
      
      const q = query(collection(db, 'tests'));
      const querySnapshot = await getDocs(q);
      const refreshedTests: Test[] = [];
      querySnapshot.forEach((doc) => {
        refreshedTests.push({
          id: doc.id,
          ...doc.data()
        } as Test);
      });
      setTests(refreshedTests);
      showNotification('Tests data refreshed successfully!', 'success');
    } catch (error: any) {
      console.error('Error refreshing tests:', error);
      showNotification(error.message || 'Failed to refresh tests', 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefreshPackages = async () => {
    try {
      setIsRefreshing(true);
      
      // Reset filters to show all data
      setSearchTerm('');
      setSelectedPackageCategory('All');
      
      const q = query(collection(db, 'healthPackages'));
      const querySnapshot = await getDocs(q);
      const refreshedPackages: Package[] = [];
      querySnapshot.forEach((doc) => {
        refreshedPackages.push({
          id: doc.id,
          ...doc.data()
        } as Package);
      });
      setPackages(refreshedPackages);
      showNotification('Packages data refreshed successfully!', 'success');
    } catch (error: any) {
      console.error('Error refreshing packages:', error);
      showNotification(error.message || 'Failed to refresh packages', 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  const showNotification = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getCategoryIcon = (category: Test['category']) => {
    switch (category) {
      case 'Blood Test': return <BloodIcon sx={{ fontSize: 24, color: '#EF4444' }} />;
      case 'Radiology': return <RadiologyIcon sx={{ fontSize: 24, color: '#8B5CF6' }} />;
      case 'Cardiology': return <CardiologyIcon sx={{ fontSize: 24, color: '#EF4444' }} />;
      case 'Biochemistry': return <BiochemistryIcon sx={{ fontSize: 24, color: '#10B981' }} />;
      case 'Pathology': return <PathologyIcon sx={{ fontSize: 24, color: '#F59E0B' }} />;
      case 'Microbiology': return <MicrobiologyIcon sx={{ fontSize: 24, color: '#6366F1' }} />;
      case 'Immunology': return <ImmunologyIcon sx={{ fontSize: 24, color: '#EC4899' }} />;
      default: return <ScienceIcon sx={{ fontSize: 24, color: '#4A90E2' }} />;
    }
  };

  const getCategoryColor = (category: Test['category']) => {
    switch (category) {
      case 'Blood Test': return '#EF4444';
      case 'Radiology': return '#8B5CF6';
      case 'Cardiology': return '#EF4444';
      case 'Biochemistry': return '#10B981';
      case 'Pathology': return '#F59E0B';
      case 'Microbiology': return '#6366F1';
      case 'Immunology': return '#EC4899';
      default: return '#4A90E2';
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSearchTerm('');
  };

  const handleAddTest = async () => {
    try {
      setIsLoading(true);
      
      // Validate required fields
      if (!testForm.name || !testForm.price) {
        showNotification('Please fill in all required fields', 'error');
        return;
      }

      const price = parseFloat(testForm.price);
      if (price <= 0) {
        showNotification('Price must be greater than 0', 'error');
        return;
      }
      
      const test = {
        name: testForm.name,
        category: testForm.category,
        price: price,
        description: testForm.description,
        preparationInstructions: testForm.preparationInstructions,
        reportTime: testForm.reportTime,
        department: testForm.department,
        sampleRequired: testForm.sampleRequired,
        testDuration: testForm.testDuration,
        isActive: testForm.isActive,
        requiresAppointment: testForm.requiresAppointment,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'tests'), test);
      
      setTestForm({
        name: '',
        category: 'Blood Test',
        price: '',
        description: '',
        preparationInstructions: '',
        reportTime: '',
        department: '',
        sampleRequired: '',
        testDuration: '30 minutes',
        isActive: true,
        requiresAppointment: false
      });
      setShowAddTestModal(false);
      showNotification('Test added successfully!', 'success');
      // Real-time listener will automatically update the tests list
    } catch (error: any) {
      console.error('Error adding test:', error);
      showNotification(error.message || 'Failed to add test', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTest = async () => {
    if (!selectedTest) return;
    
    try {
      setIsLoading(true);
      
      // Validate required fields
      if (!testForm.name || !testForm.price) {
        showNotification('Please fill in all required fields', 'error');
        return;
      }

      const price = parseFloat(testForm.price);
      if (price <= 0) {
        showNotification('Price must be greater than 0', 'error');
        return;
      }
      
      const testRef = doc(db, 'tests', selectedTest.id);
      await updateDoc(testRef, {
        name: testForm.name,
        category: testForm.category,
        price: price,
        description: testForm.description,
        preparationInstructions: testForm.preparationInstructions,
        reportTime: testForm.reportTime,
        department: testForm.department,
        sampleRequired: testForm.sampleRequired,
        testDuration: testForm.testDuration,
        isActive: testForm.isActive,
        requiresAppointment: testForm.requiresAppointment,
        updatedAt: new Date().toISOString()
      });
      
      setShowEditTestModal(false);
      setSelectedTest(null);
      showNotification('Test updated successfully!', 'success');
      // Real-time listener will automatically update the tests list
    } catch (error: any) {
      console.error('Error updating test:', error);
      showNotification(error.message || 'Failed to update test', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewTest = (test: Test) => {
    setSelectedTest(test);
    setShowViewTestModal(true);
  };

  const handleEditTestClick = (test: Test) => {
    setSelectedTest(test);
    setTestForm({
      name: test.name,
      category: test.category,
      price: test.price?.toString() || '',
      description: test.description,
      preparationInstructions: test.preparationInstructions || '',
      reportTime: test.reportTime,
      department: (test as any).department || '',
      sampleRequired: (test as any).sampleRequired || '',
      testDuration: (test as any).testDuration || '30 minutes',
      isActive: (test as any).isActive !== undefined ? (test as any).isActive : true,
      requiresAppointment: (test as any).requiresAppointment || false
    });
    setShowEditTestModal(true);
  };

  const handleDeleteTest = async (testId: string) => {
    try {
      const testRef = doc(db, 'tests', testId);
      await deleteDoc(testRef);
      
      showNotification('Test deleted successfully!', 'success');
      // Real-time listener will automatically update the tests list
    } catch (error: any) {
      console.error('Error deleting test:', error);
      showNotification(error.message || 'Failed to delete test', 'error');
    }
  };

  const handleAddPackage = async () => {
    try {
      setIsLoading(true);
      
      const healthPackage = {
        name: packageForm.name,
        description: packageForm.description,
        tests: packageForm.selectedTests,
        originalPrice: Number(packageForm.originalPrice) || 0,
        discountedPrice: Number(packageForm.discountedPrice) || 0,
        discount: Number(packageForm.discount) || 0,
        category: packageForm.category,
        duration: packageForm.duration,
        ageGroup: packageForm.ageGroup,
        benefits: packageForm.benefits,
        services: packageForm.services,
        isPopular: packageForm.isPopular,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isActive: true
      };

      await addDoc(collection(db, 'healthPackages'), healthPackage);
      
      setPackageForm({
        name: '',
        description: '',
        selectedTests: [],
        originalPrice: '',
        discountedPrice: '',
        discount: '',
        category: 'Health Checkup',
        duration: '',
        ageGroup: '',
        benefits: [],
        services: [],
        isPopular: false
      });
      setShowAddPackageModal(false);
      showNotification('Package added successfully!', 'success');
      // Real-time listener will automatically update the packages list
    } catch (error: any) {
      console.error('Error adding package:', error);
      showNotification(error.message || 'Failed to add package', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewPackage = (pkg: Package) => {
    setSelectedPackage(pkg);
    setShowViewPackageModal(true);
  };

  const handleEditPackageClick = (pkg: Package) => {
    setSelectedPackage(pkg);
    setPackageForm({
      name: pkg.name,
      description: pkg.description,
      selectedTests: pkg.tests,
      originalPrice: String(pkg.originalPrice || ''),
      discountedPrice: String(pkg.discountedPrice || ''),
      discount: String(pkg.discount || ''),
      category: pkg.category,
      duration: pkg.duration,
      ageGroup: pkg.ageGroup,
      benefits: pkg.benefits,
      services: pkg.services || [],
      isPopular: pkg.isPopular
    });
    setShowEditPackageModal(true);
  };

  const handleEditPackage = async () => {
    if (!selectedPackage) return;
    
    try {
      setIsLoading(true);
      
      const packageRef = doc(db, 'healthPackages', selectedPackage.id);
      await updateDoc(packageRef, {
        name: packageForm.name,
        description: packageForm.description,
        tests: packageForm.selectedTests,
        originalPrice: Number(packageForm.originalPrice) || 0,
        discountedPrice: Number(packageForm.discountedPrice) || 0,
        discount: Number(packageForm.discount) || 0,
        category: packageForm.category,
        duration: packageForm.duration,
        ageGroup: packageForm.ageGroup,
        benefits: packageForm.benefits,
        services: packageForm.services,
        isPopular: packageForm.isPopular,
        updatedAt: new Date().toISOString()
      });
      
      setShowEditPackageModal(false);
      setSelectedPackage(null);
      showNotification('Package updated successfully!', 'success');
      // Real-time listener will automatically update the packages list
    } catch (error: any) {
      console.error('Error updating package:', error);
      showNotification(error.message || 'Failed to update package', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePackage = async (packageId: string) => {
    try {
      const packageRef = doc(db, 'healthPackages', packageId);
      await deleteDoc(packageRef);
      
      showNotification('Package deleted successfully!', 'success');
      // Real-time listener will automatically update the packages list
    } catch (error: any) {
      console.error('Error deleting package:', error);
      showNotification(error.message || 'Failed to delete package', 'error');
    }
  };

  const totalTests = tests.length;
  const totalPackages = packages.length;
  const popularPackages = packages.filter(pkg => pkg.isPopular).length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1A202C', mb: 1 }}>
            Test & Package Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#718096' }}>
            Manage laboratory tests and health packages • Real-time updates enabled 🔄
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={activeTab === 0 ? handleRefreshTests : handleRefreshPackages}
          disabled={isRefreshing}
          sx={{ minWidth: 120 }}
        >
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary">
          <Tab 
            label="Tests" 
            icon={<ScienceIcon />} 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            label="Packages" 
            icon={<PackageIcon />} 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
        </Tabs>
      </Paper>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder={activeTab === 0 ? "Search tests..." : "Search packages..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9CA3AF' }} />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {activeTab === 0 && (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, 
          gap: 2, 
          mb: 3 
        }}>
          <Card sx={{ backgroundColor: '#4A90E2', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <BloodIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {tests.filter(t => t.category === 'Blood Test').length}
              </Typography>
              <Typography variant="body2">
                Blood Tests
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ backgroundColor: '#10B981', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <RadiologyIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {tests.filter(t => t.category === 'Radiology').length}
              </Typography>
              <Typography variant="body2">
                Radiology
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ backgroundColor: '#8B5CF6', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <CardiologyIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {tests.filter(t => t.category === 'Cardiology').length}
              </Typography>
              <Typography variant="body2">
                Cardiology
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ backgroundColor: '#EF4444', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <ScienceIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {totalTests}
              </Typography>
              <Typography variant="body2">
                Total Tests
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Package Statistics Cards */}
      {activeTab === 1 && (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, 
          gap: 2, 
          mb: 3 
        }}>
          <Card sx={{ backgroundColor: '#4A90E2', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <PackageIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {totalPackages}
              </Typography>
              <Typography variant="body2">
                Total Packages
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ backgroundColor: '#10B981', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <MedicalIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {popularPackages}
              </Typography>
              <Typography variant="body2">
                Popular Packages
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ backgroundColor: '#8B5CF6', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <CurrencyIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {Math.round(packages.reduce((acc, pkg) => acc + pkg.discount, 0) / packages.length) || 0}%
              </Typography>
              <Typography variant="body2">
                Avg. Discount
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Add Test/Package Button */}
      {activeTab === 0 && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => setShowAddTestModal(true)}
            sx={{ 
              py: 2, 
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 3,
              backgroundColor: '#4A90E2'
            }}
          >
            Add New Test
          </Button>
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => setShowAddPackageModal(true)}
            sx={{ 
              py: 2, 
              fontSize: '1.1rem',
              fontWeight: 'bold',
              borderRadius: 3,
              backgroundColor: '#10B981'
            }}
          >
            Add New Package
          </Button>
        </Box>
      )}

      {/* Category Filter - for Tests tab */}
      {activeTab === 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 'medium' }}>Filter by Category</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {categories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  onClick={() => setSelectedCategory(category)}
                  variant={selectedCategory === category ? 'filled' : 'outlined'}
                  color={selectedCategory === category ? 'primary' : 'default'}
                  size="small"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Package Category Filter - for Packages tab */}
      {activeTab === 1 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 'medium' }}>Filter by Category</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {packageCategories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  onClick={() => setSelectedPackageCategory(category)}
                  variant={selectedPackageCategory === category ? 'filled' : 'outlined'}
                  color={selectedPackageCategory === category ? 'primary' : 'default'}
                  size="small"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Content based on active tab */}
      {activeTab === 0 && (
        // Tests List
        <Box>
          {filteredTests.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <CardContent>
                <ScienceIcon sx={{ fontSize: 64, color: '#4A90E2', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No tests found
                </Typography>
                <Typography variant="body1" sx={{ color: '#718096' }}>
                  Try adjusting your search or filter criteria
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredTests.map((test) => (
                <Card key={test.id} sx={{ '&:hover': { boxShadow: 3 } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {getCategoryIcon(test.category)}
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A202C' }}>
                            {test.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#718096' }}>
                            {test.description}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: getCategoryColor(test.category) }}>
                          ₹{test.price}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#718096' }}>
                          {test.reportTime}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip 
                        label={test.category}
                        sx={{ 
                          backgroundColor: getCategoryColor(test.category),
                          color: 'white',
                          fontWeight: 'medium'
                        }}
                        size="small"
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" color="primary" onClick={() => handleViewTest(test)}>
                          <ViewIcon />
                        </IconButton>
                        <IconButton size="small" color="secondary" onClick={() => handleEditTestClick(test)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete ${test.name}?`)) {
                              handleDeleteTest(test.id);
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    {test.preparationInstructions && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#F7FAFC', borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: '#718096' }}>
                          <strong>Preparation:</strong> {test.preparationInstructions}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        // Packages List
        <Box>
          {filteredPackages.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <CardContent>
                <PackageIcon sx={{ fontSize: 64, color: '#10B981', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No packages found
                </Typography>
                <Typography variant="body1" sx={{ color: '#718096' }}>
                  Try adjusting your search or filter criteria
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredPackages.map((pkg) => (
                <Card key={pkg.id} sx={{ '&:hover': { boxShadow: 3 } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#10B981', width: 50, height: 50 }}>
                          <PackageIcon />
                        </Avatar>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A202C' }}>
                              {pkg.name}
                            </Typography>
                            {pkg.isPopular && (
                              <Chip 
                                label="Popular" 
                                size="small" 
                                icon={<StarIcon />}
                                sx={{ 
                                  backgroundColor: '#FFA500', 
                                  color: 'white',
                                  fontWeight: 'bold'
                                }} 
                              />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ color: '#718096', mb: 1 }}>
                            {pkg.description}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#718096' }}>
                            <strong>Duration:</strong> {pkg.duration} • <strong>Age Group:</strong> {pkg.ageGroup}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              textDecoration: 'line-through',
                              color: '#9CA3AF' 
                            }}
                          >
                            ₹{pkg.originalPrice}
                          </Typography>
                          <Chip 
                            label={`${pkg.discount}% OFF`}
                            size="small"
                            sx={{ 
                              backgroundColor: '#EF4444',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10B981' }}>
                          ₹{pkg.discountedPrice}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ color: '#718096', mb: 1 }}>
                        <strong>Tests included:</strong> {pkg.tests.map(test => test.name).join(', ')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#718096' }}>
                        <strong>Benefits:</strong> {pkg.benefits.slice(0, 2).join(', ')}
                        {pkg.benefits.length > 2 && ` and ${pkg.benefits.length - 2} more...`}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip 
                        label={pkg.category}
                        sx={{ 
                          backgroundColor: '#4A90E2',
                          color: 'white',
                          fontWeight: 'medium'
                        }}
                        size="small"
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" color="primary" onClick={() => handleViewPackage(pkg)}>
                          <ViewIcon />
                        </IconButton>
                        <IconButton size="small" color="secondary" onClick={() => handleEditPackageClick(pkg)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete ${pkg.name}?`)) {
                              handleDeletePackage(pkg.id);
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Add Test Modal */}
      <Dialog
        open={showAddTestModal}
        onClose={() => setShowAddTestModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Test</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Test Name *"
              value={testForm.name}
              onChange={(e) => setTestForm({ ...testForm, name: e.target.value })}
            />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={testForm.category}
                  onChange={(e) => setTestForm({ ...testForm, category: e.target.value as Test['category'] })}
                  label="Category *"
                >
                  {categories.slice(1).map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Price (₹) *"
                value={testForm.price}
                onChange={(e) => setTestForm({ ...testForm, price: e.target.value })}
                placeholder="0"
                type="number"
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                label="Department"
                value={testForm.department}
                onChange={(e) => setTestForm({ ...testForm, department: e.target.value })}
                placeholder="e.g., Lab, Radiology"
              />
              
              <TextField
                fullWidth
                label="Sample Required"
                value={testForm.sampleRequired}
                onChange={(e) => setTestForm({ ...testForm, sampleRequired: e.target.value })}
                placeholder="e.g., Blood, Urine"
              />
            </Box>

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={testForm.description}
              onChange={(e) => setTestForm({ ...testForm, description: e.target.value })}
              placeholder="Enter test description"
            />

            <TextField
              fullWidth
              label="Preparation Instructions"
              multiline
              rows={2}
              value={testForm.preparationInstructions}
              onChange={(e) => setTestForm({ ...testForm, preparationInstructions: e.target.value })}
              placeholder="Enter preparation instructions"
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                label="Test Duration"
                value={testForm.testDuration}
                onChange={(e) => setTestForm({ ...testForm, testDuration: e.target.value })}
                placeholder="e.g., 30 minutes"
              />
              
              <TextField
                fullWidth
                label="Report Time"
                value={testForm.reportTime}
                onChange={(e) => setTestForm({ ...testForm, reportTime: e.target.value })}
                placeholder="e.g., 24 hours"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={testForm.isActive}
                    onChange={(e) => setTestForm({ ...testForm, isActive: e.target.checked })}
                  />
                }
                label="Test is Active"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={testForm.requiresAppointment}
                    onChange={(e) => setTestForm({ ...testForm, requiresAppointment: e.target.checked })}
                  />
                }
                label="Requires Appointment"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddTestModal(false)}>Cancel</Button>
          <Button 
            onClick={handleAddTest} 
            variant="contained"
            disabled={!testForm.name || !testForm.price || isLoading}
          >
            {isLoading ? 'Adding...' : 'Add Test'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Test Modal */}
      <Dialog
        open={showViewTestModal}
        onClose={() => {
          setShowViewTestModal(false);
          setSelectedTest(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {selectedTest && getCategoryIcon(selectedTest.category)}
          Test Details
        </DialogTitle>
        <DialogContent>
          {selectedTest && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {selectedTest.name}
                </Typography>
                <Chip 
                  label={selectedTest.category}
                  sx={{ 
                    backgroundColor: getCategoryColor(selectedTest.category),
                    color: 'white',
                    fontWeight: 'medium'
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096' }}>
                  Description
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedTest.description}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096' }}>
                  Price
                </Typography>
                <Typography variant="h6" sx={{ color: getCategoryColor(selectedTest.category), fontWeight: 'bold' }}>
                  ₹{selectedTest.price}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096' }}>
                  Report Time
                </Typography>
                <Typography variant="body1">
                  {selectedTest.reportTime}
                </Typography>
              </Box>

              {selectedTest.preparationInstructions && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096' }}>
                    Preparation Instructions
                  </Typography>
                  <Box sx={{ p: 2, backgroundColor: '#F7FAFC', borderRadius: 1 }}>
                    <Typography variant="body2">
                      {selectedTest.preparationInstructions}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowViewTestModal(false);
            setSelectedTest(null);
          }}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<EditIcon />}
            onClick={() => {
              setShowViewTestModal(false);
              if (selectedTest) {
                handleEditTestClick(selectedTest);
              }
            }}
          >
            Edit Test
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Test Modal */}
      <Dialog
        open={showEditTestModal}
        onClose={() => {
          setShowEditTestModal(false);
          setSelectedTest(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Test</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Test Name *"
              value={testForm.name}
              onChange={(e) => setTestForm({ ...testForm, name: e.target.value })}
            />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                label="Price (₹) *"
                value={testForm.price}
                onChange={(e) => setTestForm({ ...testForm, price: e.target.value })}
                placeholder="0"
                type="number"
              />
              
              <FormControl fullWidth>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={testForm.category}
                  onChange={(e) => setTestForm({ ...testForm, category: e.target.value as Test['category'] })}
                >
                  {categories.slice(1).map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                label="Department"
                value={testForm.department}
                onChange={(e) => setTestForm({ ...testForm, department: e.target.value })}
                placeholder="e.g., Lab, Radiology"
              />
              
              <TextField
                fullWidth
                label="Sample Required"
                value={testForm.sampleRequired}
                onChange={(e) => setTestForm({ ...testForm, sampleRequired: e.target.value })}
                placeholder="e.g., Blood, Urine"
              />
            </Box>

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={testForm.description}
              onChange={(e) => setTestForm({ ...testForm, description: e.target.value })}
              placeholder="Enter test description"
            />

            <TextField
              fullWidth
              label="Preparation Instructions"
              multiline
              rows={2}
              value={testForm.preparationInstructions}
              onChange={(e) => setTestForm({ ...testForm, preparationInstructions: e.target.value })}
              placeholder="Enter preparation instructions"
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                label="Test Duration"
                value={testForm.testDuration}
                onChange={(e) => setTestForm({ ...testForm, testDuration: e.target.value })}
                placeholder="e.g., 30 minutes"
              />
              
              <TextField
                fullWidth
                label="Report Time"
                value={testForm.reportTime}
                onChange={(e) => setTestForm({ ...testForm, reportTime: e.target.value })}
                placeholder="e.g., 24 hours"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={testForm.isActive}
                    onChange={(e) => setTestForm({ ...testForm, isActive: e.target.checked })}
                  />
                }
                label="Test is Active"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={testForm.requiresAppointment}
                    onChange={(e) => setTestForm({ ...testForm, requiresAppointment: e.target.checked })}
                  />
                }
                label="Requires Appointment"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowEditTestModal(false);
            setSelectedTest(null);
          }}>Cancel</Button>
          <Button 
            onClick={handleEditTest} 
            variant="contained"
            disabled={!testForm.name || !testForm.price || isLoading}
          >
            {isLoading ? 'Updating...' : 'Update Test'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Package Modal */}
      <Dialog
        open={showAddPackageModal}
        onClose={() => {
          setShowAddPackageModal(false);
          setPackageForm({
            name: '',
            description: '',
            selectedTests: [],
            originalPrice: '',
            discountedPrice: '',
            discount: '',
            category: 'Health Checkup',
            duration: '',
            ageGroup: '',
            benefits: [],
            services: [],
            isPopular: false
          });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Package</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Package Name *"
              value={packageForm.name}
              onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
            />

            <TextField
              fullWidth
              label="Description *"
              multiline
              rows={2}
              value={packageForm.description}
              onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
            />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={packageForm.category}
                  onChange={(e) => setPackageForm({ ...packageForm, category: e.target.value as Package['category'] })}
                  label="Category *"
                >
                  {packageCategories.slice(1).map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Services</InputLabel>
                <Select
                  multiple
                  value={packageForm.services}
                  onChange={(e) => setPackageForm({ ...packageForm, services: typeof e.target.value === 'string' ? [e.target.value] : e.target.value })}
                  label="Services"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="Blood Tests">
                    <Checkbox checked={packageForm.services.indexOf("Blood Tests") > -1} />
                    <ListItemText primary="Blood Tests" />
                  </MenuItem>
                  <MenuItem value="Radiology Tests">
                    <Checkbox checked={packageForm.services.indexOf("Radiology Tests") > -1} />
                    <ListItemText primary="Radiology Tests" />
                  </MenuItem>
                  <MenuItem value="Cardiac Tests">
                    <Checkbox checked={packageForm.services.indexOf("Cardiac Tests") > -1} />
                    <ListItemText primary="Cardiac Tests" />
                  </MenuItem>
                  <MenuItem value="Advanced Tests">
                    <Checkbox checked={packageForm.services.indexOf("Advanced Tests") > -1} />
                    <ListItemText primary="Advanced Tests" />
                  </MenuItem>
                  <MenuItem value="Urine Test">
                    <Checkbox checked={packageForm.services.indexOf("Urine Test") > -1} />
                    <ListItemText primary="Urine Test" />
                  </MenuItem>
                  <MenuItem value="Stool Test">
                    <Checkbox checked={packageForm.services.indexOf("Stool Test") > -1} />
                    <ListItemText primary="Stool Test" />
                  </MenuItem>
                  <MenuItem value="Emergency Test">
                    <Checkbox checked={packageForm.services.indexOf("Emergency Test") > -1} />
                    <ListItemText primary="Emergency Test" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Original Price (₹) *"
                value={packageForm.originalPrice}
                onChange={(e) => setPackageForm({ ...packageForm, originalPrice: e.target.value })}
                placeholder="Enter original price"
              />

              <TextField
                fullWidth
                type="number"
                label="Discounted Price (₹) *"
                value={packageForm.discountedPrice}
                onChange={(e) => setPackageForm({ ...packageForm, discountedPrice: e.target.value })}
                placeholder="Enter discounted price"
              />

              <TextField
                fullWidth
                type="number"
                label="Discount (%)"
                value={packageForm.discount}
                onChange={(e) => setPackageForm({ ...packageForm, discount: e.target.value })}
                placeholder="Enter discount %"
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                label="Duration *"
                value={packageForm.duration}
                onChange={(e) => setPackageForm({ ...packageForm, duration: e.target.value })}
                placeholder="e.g., 4-6 hours"
              />

              <TextField
                fullWidth
                label="Age Group *"
                value={packageForm.ageGroup}
                onChange={(e) => setPackageForm({ ...packageForm, ageGroup: e.target.value })}
                placeholder="e.g., 18-60 years"
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={packageForm.isPopular}
                  onChange={(e) => setPackageForm({ ...packageForm, isPopular: e.target.checked })}
                />
              }
              label="Mark as Popular Package"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowAddPackageModal(false);
            setPackageForm({
              name: '',
              description: '',
              selectedTests: [],
              originalPrice: '',
              discountedPrice: '',
              discount: '',
              category: 'Health Checkup',
              duration: '',
              ageGroup: '',
              benefits: [],
              services: [],
              isPopular: false
            });
          }}>Cancel</Button>
          <Button 
            onClick={handleAddPackage} 
            variant="contained"
            disabled={!packageForm.name || !packageForm.description || !packageForm.originalPrice || !packageForm.discountedPrice || isLoading}
          >
            {isLoading ? 'Adding...' : 'Add Package'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Package Modal */}
      <Dialog
        open={showViewPackageModal}
        onClose={() => {
          setShowViewPackageModal(false);
          setSelectedPackage(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PackageIcon />
          Package Details
        </DialogTitle>
        <DialogContent>
          {selectedPackage && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {selectedPackage.name}
                  </Typography>
                  {selectedPackage.isPopular && (
                    <Chip 
                      label="Popular" 
                      size="small" 
                      icon={<StarIcon />}
                      sx={{ 
                        backgroundColor: '#FFA500', 
                        color: 'white',
                        fontWeight: 'bold'
                      }} 
                    />
                  )}
                </Box>
                <Chip 
                  label={selectedPackage.category}
                  sx={{ 
                    backgroundColor: '#4A90E2',
                    color: 'white',
                    fontWeight: 'medium'
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096' }}>
                  Description
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedPackage.description}
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096' }}>
                    Pricing
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        textDecoration: 'line-through',
                        color: '#9CA3AF' 
                      }}
                    >
                      ₹{selectedPackage.originalPrice}
                    </Typography>
                    <Chip 
                      label={`${selectedPackage.discount}% OFF`}
                      size="small"
                      sx={{ 
                        backgroundColor: '#EF4444',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                  <Typography variant="h5" sx={{ color: '#10B981', fontWeight: 'bold' }}>
                    ₹{selectedPackage.discountedPrice}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096' }}>
                    Package Details
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Duration:</strong> {selectedPackage.duration}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Age Group:</strong> {selectedPackage.ageGroup}
                  </Typography>
                  {selectedPackage.services && selectedPackage.services.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Services:</strong>
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedPackage.services.map((service, index) => (
                          <Chip 
                            key={index} 
                            label={service} 
                            size="small"
                            sx={{ backgroundColor: '#E0E7FF', color: '#4338CA' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096', mb: 1 }}>
                  Tests Included
                </Typography>
                {selectedPackage.tests && selectedPackage.tests.length > 0 ? (
                  <List dense>
                    {selectedPackage.tests.map((test, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon>
                          {getCategoryIcon(test.category)}
                        </ListItemIcon>
                        <ListItemText
                          primary={test.name}
                          secondary={test.description}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: getCategoryColor(test.category) }}>
                          ₹{test.price}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                ) : selectedPackage.services && selectedPackage.services.length > 0 ? (
                  <List dense>
                    {selectedPackage.services.map((service, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon>
                          <MedicalIcon sx={{ color: '#4338CA' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={service}
                          secondary="Service Category"
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No tests or services added yet
                  </Typography>
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#718096', mb: 1 }}>
                  Benefits
                </Typography>
                <List dense>
                  {selectedPackage.benefits.map((benefit, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                      <Typography variant="body2">• {benefit}</Typography>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowViewPackageModal(false);
            setSelectedPackage(null);
          }}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<EditIcon />}
            onClick={() => {
              setShowViewPackageModal(false);
              if (selectedPackage) {
                handleEditPackageClick(selectedPackage);
              }
            }}
          >
            Edit Package
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Package Modal */}
      <Dialog
        open={showEditPackageModal}
        onClose={() => {
          setShowEditPackageModal(false);
          setSelectedPackage(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Package</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Package Name *"
              value={packageForm.name}
              onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
            />

            <TextField
              fullWidth
              label="Description *"
              multiline
              rows={2}
              value={packageForm.description}
              onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
            />
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={packageForm.category}
                  onChange={(e) => setPackageForm({ ...packageForm, category: e.target.value as Package['category'] })}
                  label="Category *"
                >
                  {packageCategories.slice(1).map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Services</InputLabel>
                <Select
                  multiple
                  value={packageForm.services}
                  onChange={(e) => setPackageForm({ ...packageForm, services: typeof e.target.value === 'string' ? [e.target.value] : e.target.value })}
                  label="Services"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="Blood Tests">
                    <Checkbox checked={packageForm.services.indexOf("Blood Tests") > -1} />
                    <ListItemText primary="Blood Tests" />
                  </MenuItem>
                  <MenuItem value="Radiology Tests">
                    <Checkbox checked={packageForm.services.indexOf("Radiology Tests") > -1} />
                    <ListItemText primary="Radiology Tests" />
                  </MenuItem>
                  <MenuItem value="Cardiac Tests">
                    <Checkbox checked={packageForm.services.indexOf("Cardiac Tests") > -1} />
                    <ListItemText primary="Cardiac Tests" />
                  </MenuItem>
                  <MenuItem value="Advanced Tests">
                    <Checkbox checked={packageForm.services.indexOf("Advanced Tests") > -1} />
                    <ListItemText primary="Advanced Tests" />
                  </MenuItem>
                  <MenuItem value="Urine Test">
                    <Checkbox checked={packageForm.services.indexOf("Urine Test") > -1} />
                    <ListItemText primary="Urine Test" />
                  </MenuItem>
                  <MenuItem value="Stool Test">
                    <Checkbox checked={packageForm.services.indexOf("Stool Test") > -1} />
                    <ListItemText primary="Stool Test" />
                  </MenuItem>
                  <MenuItem value="Emergency Test">
                    <Checkbox checked={packageForm.services.indexOf("Emergency Test") > -1} />
                    <ListItemText primary="Emergency Test" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                type="number"
                label="Original Price (₹) *"
                value={packageForm.originalPrice}
                onChange={(e) => setPackageForm({ ...packageForm, originalPrice: e.target.value })}
                placeholder="Enter original price"
              />

              <TextField
                fullWidth
                type="number"
                label="Discounted Price (₹) *"
                value={packageForm.discountedPrice}
                onChange={(e) => setPackageForm({ ...packageForm, discountedPrice: e.target.value })}
                placeholder="Enter discounted price"
              />

              <TextField
                fullWidth
                type="number"
                label="Discount (%)"
                value={packageForm.discount}
                onChange={(e) => setPackageForm({ ...packageForm, discount: e.target.value })}
                placeholder="Enter discount %"
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                label="Duration *"
                value={packageForm.duration}
                onChange={(e) => setPackageForm({ ...packageForm, duration: e.target.value })}
                placeholder="e.g., 4-6 hours"
              />

              <TextField
                fullWidth
                label="Age Group *"
                value={packageForm.ageGroup}
                onChange={(e) => setPackageForm({ ...packageForm, ageGroup: e.target.value })}
                placeholder="e.g., 18-60 years"
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={packageForm.isPopular}
                  onChange={(e) => setPackageForm({ ...packageForm, isPopular: e.target.checked })}
                />
              }
              label="Mark as Popular Package"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowEditPackageModal(false);
            setSelectedPackage(null);
          }}>Cancel</Button>
          <Button 
            onClick={handleEditPackage} 
            variant="contained"
            disabled={!packageForm.name || !packageForm.description || !packageForm.originalPrice || !packageForm.discountedPrice || isLoading}
          >
            {isLoading ? 'Updating...' : 'Update Package'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Tests;